import { createFileRoute } from "@tanstack/react-router";
import NavbarWebsite from "../components/NavbarWebsite";
import NavbarFooter from "../components/Footer";
import { JSX } from "react";

export const Route = createFileRoute("/terms-of-service")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <div className="min-h-screen bg-white text-gray-800">
        <NavbarWebsite />
        <div className=" max-w-3xl mx-auto mt-20">
          <p className="text-4xl font-extrabold text-center mb-4 pt-9 ">
            Terms of Service
          </p>
          <p className="text-l font-normal text-center mb-4 pb-2">
            Effective date: July 8, 2025
          </p>
        </div>

        <div className="max-w-3xl mx-auto px-4 pb-20 space-y-10 text-left">
          <Section id="introduction" title="1. Introduction">
            <p>
              Welcome to Uxhulu. These Terms of Service ("Terms") govern your
              access to and use of Uxhulu ("the Service"), a software platform
              that uses OpenAI’s ChatGPT service to generate content based on
              user inputs, including uploaded images and custom prompts. By
              using Uxhulu, you agree to be bound by these Terms.
            </p>
          </Section>

          <Section id="definitions" title="2. Definitions">
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>"Service"</strong> refers to the Uxhulu platform.
              </li>
              <li>
                <strong>"User"</strong> or <strong>"you"</strong> refers to any
                individual or entity using Uxhulu.
              </li>
              <li>
                <strong>"Generated services"</strong> refers to OpenAI’s
                ChatGPT service integrated into Uxhulu.
              </li>
              <li>
                <strong>"User Content"</strong> refers to any text, images, or
                other content you submit or upload.
              </li>
            </ul>
          </Section>

          <Section id="eligibility" title="3. User Eligibility">
            <p>
              You must be at least 18 years old to use Uxhulu. By using the
              Service, you affirm that you are of legal age and have the
              authority to enter into these Terms.
            </p>
          </Section>

          <Section id="ai-features" title="4. Use of generated features">
            <p>
              Uxhulu is powered by generative artificial intelligence. The
              Service uses OpenAI’s ChatGPT service to process your prompts
              and uploaded content, and generate responses or content
              accordingly.
            </p>
            <p>
              <strong>Disclaimer:</strong> generated content may contain
              inaccuracies, offensive content, or bias. Uxhulu does not
              guarantee the factual accuracy or legality of generated outputs.
              You are solely responsible for verifying the content before
              relying on or sharing it.
            </p>
            <p>
              You acknowledge and accept these risks by using the Service.
              Uxhulu is not responsible for any consequences arising from your
              reliance on generated content.
            </p>
          </Section>

          <Section id="user-content" title="5. User Content">
            <p>
              You retain ownership of the content you upload or input, including
              images and prompt text. However, by using the Service:
            </p>
            <ul className="list-disc list-inside ml-4">
              <li>
                You grant Uxhulu a perpetual, worldwide, non-exclusive,
                royalty-free, sublicensable license to use, display, distribute,
                reproduce, and modify your content solely to operate and improve
                the Service.
              </li>
              <li>
                You grant OpenAI a right to process your content as required by
                their service, in accordance with{" "}
                <a
                  href="https://openai.com/policies/terms-of-use/"
                  className="text-blue-600 underline"
                >
                  OpenAI’s terms of use
                </a>
                .
              </li>
            </ul>
          </Section>

          <Section id="acceptable-use" title="6. Acceptable Use Policy">
            <p>You agree not to use Uxhulu to:</p>
            <ul className="list-disc list-inside ml-4">
              <li>
                Upload or generate content that is illegal, harmful, abusive,
                defamatory, harassing, hateful, obscene, or misleading.
              </li>
              <li>
                Attempt to reverse-engineer, misuse, or manipulate the service
                to produce unsafe or harmful outputs.
              </li>
              <li>
                Upload images of minors or any content that infringes on others'
                rights.
              </li>
            </ul>
            <p>Violations may result in immediate termination of access.</p>
          </Section>

          <Section id="privacy" title="7. Data Handling and Privacy">
            <p>
              Inputs and outputs may be temporarily logged by Uxhulu and its
              subprocessors to monitor abuse, diagnose issues, and improve
              performance. Logged data is retained for no more than 30 days
              unless required by law.
            </p>
            <p>
              Data is encrypted in transit and at rest. Service subprocessors,
              including OpenAI, do not use submitted content to train their
              models.
            </p>
            <p>
              See our{" "}
              <a href="/privacy-policy" className="text-blue-600 underline">
                Privacy Policy
              </a>{" "}
              for full details on how your data is handled.
            </p>
          </Section>

          <Section id="ip" title="8. Intellectual Property">
            <p>
              All software, branding, and platform content (excluding user
              content) are the intellectual property of Uxhulu or its
              licensors. You may not copy, distribute, or create derivative
              works without permission.
            </p>
            <p>
              Generated content may have unclear or shared ownership. Uxhulu
              does not guarantee exclusivity or originality of content created
              through model services.
            </p>
          </Section>

          <Section id="warranty" title="9. Disclaimer of Warranties">
            <p>
              The Service is provided “as is” and “as available.” Uxhulu makes
              no warranties, express or implied, including the implied
              warranties of merchantability, fitness for a particular purpose,
              and non-infringement.
            </p>
          </Section>

          <Section id="liability" title="10. Limitation of Liability">
            <p>
              To the maximum extent permitted by law, Uxhulu shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, or any loss of profits, data, or goodwill
              arising from your use of the Service.
            </p>
          </Section>

          <Section id="termination" title="11. Termination">
            <p>
              We may suspend or terminate your access at any time if you violate
              these Terms or misuse the Service. Upon termination, your access
              will cease, and we may delete your content after a retention
              period.
            </p>
          </Section>

          <Section id="modifications" title="12. Modifications to Terms">
            <p>
              We may update these Terms at any time. If we make significant
              changes, we will notify you via the platform or email. Your
              continued use after changes means you accept the new Terms.
            </p>
          </Section>

          <Section id="law" title="13. Governing Law">
            <p>
              These Terms are governed by the laws of [Your State/Country]. You
              agree to submit to the exclusive jurisdiction of the courts
              located in [Your City, State].
            </p>
          </Section>

          <Section
            id="arbitration"
            title="14. Dispute Resolution and Arbitration"
          >
            <p>
              Any disputes arising out of these Terms or use of the Service
              shall be resolved through binding arbitration in accordance with
              the rules of the American Arbitration Association (or similar
              body). You agree to waive your right to a jury trial.
            </p>
          </Section>

          <Section id="class-action-waiver" title="15. Class Action Waiver">
            <p>
              You agree that any disputes will be resolved on an individual
              basis and waive your right to participate in any class action
              lawsuits or class-wide arbitration.
            </p>
          </Section>

          <Section id="third-party" title="16. Third-Party Services">
            <p>
              The Service may link to or integrate third-party services. We do
              not control and are not responsible for the content, policies, or
              practices of these third-party services.
            </p>
          </Section>

          <Section id="export-controls" title="17. Export Controls">
            <p>
              You agree to comply with all applicable export control laws and
              regulations. You may not use or export the Service in violation of
              U.S. law or other applicable laws.
            </p>
          </Section>

          <Section id="child-protection" title="18. Children’s Privacy">
            <p>
              The Service is not intended for children under the age of 13. We
              do not knowingly collect personal data from children under 13. If
              we learn that we have done so, we will delete the data promptly.
            </p>
          </Section>

          <Section id="dmca" title="19. Copyright and takedown policy">
            <p>
              If you believe content on Uxhulu infringes your copyright, please
              contact us at [your-email@example.com] with a detailed takedown
              notice. We will respond appropriately and remove content where
              necessary.
            </p>
          </Section>

          <Section
            id="entire-agreement"
            title="20. Entire Agreement and Severability"
          >
            <p>
              These Terms constitute the entire agreement between you and
              Uxhulu. If any part of these Terms is found invalid or
              unenforceable, the remainder shall remain in full force and
              effect.
            </p>
          </Section>

          <Section id="contact" title="21. Contact Us">
            <p>If you have any questions, contact us at:</p>
            <ul className="list-inside ml-4">
              <li>Email: [your-email@example.com]</li>
            </ul>
          </Section>

          <Section
            id="username-policy"
            title="22. Username and Account Naming Policy"
          >
            <p>
              Uxhulu reserves the right to review, suspend, or reclaim any
              username at our sole discretion, including but not limited to
              cases where:
            </p>
            <ul className="list-disc list-inside ml-4">
              <li>
                The username is misleading, impersonates another person, brand,
                or entity (e.g., using names like “Google”, “Support”, or public
                figures).
              </li>
              <li>
                The username contains offensive, abusive, hateful, or
                inappropriate language.
              </li>
              <li>
                The username violates intellectual property rights, trademarks,
                or creates confusion with established entities.
              </li>
            </ul>
            <p>
              If your username is subject to dispute or reported, we may require
              you to change it or may reassign it without prior notice.
            </p>
          </Section>

          <Section
            id="service-termination"
            title="23. Service Availability and Termination by Us"
          >
            <p>
              We reserve the right to modify, suspend, or discontinue the
              Service (or any part thereof) at any time, with or without notice,
              and for any reason, including but not limited to operational,
              legal, technical, or business reasons.
            </p>
            <p>
              We are not liable to you or any third party for any modification,
              suspension, or discontinuation of the Service. We recommend that
              you maintain backups of any content you may need, as access may
              not be recoverable once the Service is closed.
            </p>
          </Section>
        </div>
        <NavbarFooter />
      </div>
    </>
  );
}

function Section(props: {
  id: string;
  title: string;
  children: JSX.Element | JSX.Element[];
}) {
  const { id, title, children } = props;

  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      <div className="text-base space-y-2">{children}</div>
    </section>
  );
}
